import { useEffect, useState } from "react";
import { api, leerSesion, ErrorApi } from "../api.ts";
import { tamanoLegible, aLocal } from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";

interface Estado { estado: "ninguna" | "en_curso" | "lista" | "error"; id?: string; hecho?: number; total?: number; bytes?: number; error?: string; vence?: string; nombre?: string }

/** Exportar todas las historias clínicas de la clínica en un ZIP (un PDF para leer y un JSON por paciente, más un índice para Excel). */
export function ExportarHistorias() {
  const [e, setE] = useState<Estado | null>(null);
  const a = useAccion();
  const cargar = async () => { const r = await a.correr(() => api<Estado>("GET", "/api/exportacion-historias")); if (r) setE(r); return r; };
  useEffect(() => { cargar(); }, []);
  // mientras se arma se consulta cada 1,5 segundos
  useEffect(() => {
    if (e?.estado !== "en_curso") return;
    const t = setTimeout(cargar, 1500);
    return () => clearTimeout(t);
  }, [e]);

  async function exportar() {
    if (!confirm("Se armará un archivo con las historias clínicas de TODOS los pacientes de tu clínica. Contiene datos de salud: guárdalo en un lugar seguro y bórralo cuando ya no lo necesites. ¿Continuar?")) return;
    if (await a.correr(() => api("POST", "/api/exportacion-historias"))) await cargar();
  }
  async function descargar() {
    if (!e?.id) return;
    const s = leerSesion();
    await a.correr(async () => {
      const r = await fetch(`/api/exportacion-historias/${e.id}/descargar`, { headers: s ? { authorization: `Bearer ${s.token}` } : {} });
      if (!r.ok) throw new ErrorApi(r.status, ((await r.json().catch(() => ({}))) as { error?: string }).error ?? "No se pudo descargar.");
      const url = URL.createObjectURL(await r.blob());
      const enlace = document.createElement("a"); enlace.href = url; enlace.download = e.nombre ?? "Historias-clinicas.zip"; enlace.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    });
  }
  async function borrar() {
    if (!e?.id || !confirm("¿Borrar este archivo del servidor? Si ya lo descargaste, tu copia no se toca.")) return;
    const r = await a.correr(() => api<Estado>("DELETE", `/api/exportacion-historias/${e.id}`));
    if (r) setE(r);
  }

  const enCurso = e?.estado === "en_curso";
  const vence = e?.vence ? aLocal(new Date(e.vence)).hora : "";
  return (
    <Seccion titulo="Exportar todas las historias clínicas" acciones={<button className="btn pri" onClick={exportar} disabled={a.ocupado || enCurso || !e}>{enCurso ? "Armando…" : "Exportar en ZIP"}</button>}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Genera un archivo ZIP con <b>un PDF por paciente</b> (filiación, historia clínica, diagnósticos, evolución, plan, pagos, recetas, consentimientos y certificados), el mismo contenido en formato JSON para pasarlo a otro sistema
        y un <b>índice para Excel</b>. No incluye las fotografías ni las radiografías (esas van en la copia de seguridad) ni a los pacientes de prueba. El PDF es para leer: no lleva firma digital.
        Queda registrado quién lo exportó y quién lo descargó (registro de actividad y alertas de seguridad).
      </p>
      <AvisoError error={a.error} />
      {enCurso && (
        <div className="aviso info" role="status">
          Armando el archivo: {e?.hecho ?? 0} de {e?.total || "…"} pacientes. Puedes seguir trabajando.
          {!!e?.total && <progress value={e.hecho ?? 0} max={e.total} style={{ display: "block", width: "100%", marginTop: 6 }} />}
        </div>
      )}
      {e?.estado === "error" && <div className="aviso error" role="alert">No se pudo armar el archivo: {e.error}</div>}
      {e?.estado === "lista" && (
        <div className="aviso ok" role="status">
          <b>Listo:</b> {e.nombre} · {tamanoLegible(e.bytes ?? 0)} · {e.total} pacientes. Estará disponible hasta las {vence}.
          <div className="fila" style={{ marginTop: 8, flexWrap: "wrap" }}>
            <button className="btn pri" onClick={descargar} disabled={a.ocupado}>Descargar</button>
            <button className="btn" onClick={borrar} disabled={a.ocupado}>Borrar del servidor</button>
          </div>
        </div>
      )}
    </Seccion>
  );
}
